import { useState } from 'react'
import Lottie from 'react-lottie'
import { defaultOptionsEmojiPicker } from 'src/utils/constants'
import { addReaction, deleteReaction } from 'src/client/publication'
import { Reaction } from 'src/hook/usePosts'
import usePublicCommunity from 'src/hook/usePublicCommunity'
import { EmojiUtils } from 'src/utils/emoji'
import clsx from 'clsx'
import { ComponentSources, ScreenNames } from 'src/utils/datahub/enums'
import { paths } from 'src/router/paths'
import { useMatch } from 'react-router-dom'
import usePublicCommunityReactionClick from 'src/hook/usePublicCommunityReactionClick/usePublicCommunityReactionClick'
import PillButton from '../pillButton'
import { ReactionsOption } from '../reactionsBar.types'
import { ReactionsListProps } from './types'
import { ContainerLottie } from './reactionsList.styles'

const ReactionsList = ({
  showAnimation,
  reactions,
  setReactions,
  heartCode,
  postId,
  onReactEvent,
  onReactionSuccess,
  onReactionError,
  onReaction
}: ReactionsListProps) => {
  const [loading, setLoading] = useState(false)
  const { setScreenName, setComponentSource } = usePublicCommunity()
  const { shouldCallReactionClick, handlePublicCommunityReactionClick } =
    usePublicCommunityReactionClick()

  const screenName = useMatch(paths.COMMUNITY_POST_PATH)
    ? ScreenNames.COMMUNITY_POST_DETAIL
    : ScreenNames.COMMUNITY_FEED

  const handleClickEmoji = async (reaction: Reaction) => {
    onReactEvent(reaction.reactedByUser ? ReactionsOption.UNLIKE : ReactionsOption.LIKE)

    onReaction?.()

    if (shouldCallReactionClick) {
      setScreenName(screenName)
      setComponentSource(ComponentSources.REACTION_BAR)

      handlePublicCommunityReactionClick()
      return
    }

    setLoading(true)

    if (reaction.reactedByUser) {
      const removeReaction = reactions?.filter(
        (react) => react.reactionCode !== reaction.reactionCode
      )

      setReactions(() => {
        if (reaction.count === 1 && reaction.reactionCode !== heartCode) {
          return removeReaction
        }

        return reactions?.map((react) => {
          if (react.reactionCode === reaction.reactionCode) {
            return {
              reactionCode: reaction.reactionCode,
              count: reaction.count - 1,
              reactedByUser: false
            }
          }

          return react
        })
      })
      await deleteReaction(postId, reaction.reactionCode)
      setLoading(false)
      return
    }

    try {
      const hasReaction = reactions?.find((react) => react.reactionCode === reaction.reactionCode)

      setReactions((prevState) => {
        if (hasReaction) {
          return reactions?.map((react) => {
            if (react.reactionCode === reaction.reactionCode) {
              return {
                reactionCode: reaction.reactionCode,
                count: reaction.count + 1,
                reactedByUser: true
              }
            }

            return react
          })
        }

        return [...prevState, reaction]
      })

      await addReaction(postId, reaction.reactionCode)
      onReactionSuccess?.()
    } catch (error) {
      const err = error as { statusCode: number; message: string }

      onReactionError?.({
        statusCode: err.statusCode,
        errorMessage: err.message
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {reactions?.map((reactionRefreshed, i) => (
        <PillButton
          key={i}
          testId="pill-reaction"
          ariaLabel="reaction"
          disabled={loading}
          handleClick={() => handleClickEmoji(reactionRefreshed)}
          active={reactionRefreshed.reactedByUser}
          className={clsx('_position-relative _text-gray-700', {
            '_font-weight-bold': reactionRefreshed.reactedByUser
          })}
        >
          <span>{EmojiUtils.convertEmojiCodeReceive(reactionRefreshed.reactionCode)}</span>
          {i === 0 && showAnimation && (
            <ContainerLottie data-test="container-lottie" className="_position-absolute">
              <Lottie options={defaultOptionsEmojiPicker} height={40} width={40} />
            </ContainerLottie>
          )}
          <span className="_pl-2">{reactionRefreshed.count}</span>
        </PillButton>
      ))}
    </>
  )
}

export default ReactionsList
